import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Heart, ArrowLeft } from 'lucide-react'

export default function About() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      {/* Navigation Bar */}
      <nav className="sticky top-0 z-50 border-b bg-white/80 backdrop-blur-sm dark:bg-gray-950/80 dark:border-gray-800">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600">
                <Heart className="h-6 w-6 text-white" />
              </div>
              <span className="text-xl font-semibold">Breakthrough Health</span>
            </div>
            <Button variant="ghost" onClick={() => router.visit('/')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="mx-auto max-w-4xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl mb-6">
            Our Why
          </h1>
          <div className="h-1 w-24 mx-auto bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-full"></div>
        </div>

        {/* Main Content */}
        <article className="prose prose-lg dark:prose-invert max-w-none">
          <div className="space-y-8 text-lg leading-relaxed text-foreground">
            <section>
              <h2 className="text-3xl font-bold mb-6">A Cure Within Reach, A System Out of Touch</h2>
              <p className="mb-4">
                It's a strange irony of modern medicine: we possess the power to cure, but not the systems to deliver it. In an age where artificial intelligence can compose symphonies and forecast weather with staggering precision, we still let hundreds of thousands die from a disease we know how to cure. Hepatitis C is not an unsolvable riddle. It's not a medical mystery. It is a curable disease, trapped inside an incurable system failure.
              </p>
              <p className="mb-4">
                But that failure is not inevitable.
              </p>
            </section>

            <section>
              <p className="mb-4">
                We believe the true test of innovation isn't whether we can invent new miracles, it's whether we can bring known ones to those who need them most. Curing Hepatitis C does not require new science. It requires new infrastructure. It requires the courage to reach into the broken seams of our public health system and stitch together a path from diagnosis to cure, especially for those who have been ignored, overlooked, or forgotten.
              </p>
            </section>

            <section>
              <p className="mb-4">
                We know who they are: the man recently released from incarceration with no primary care. The woman struggling with substance use, cycling in and out of shelters. The child of immigrants who never gets tested because no one ever asks. These are not edge cases. They are the very center of the crisis. And they are the reason we are building Breakthrough Health. If not us, then who?
              </p>
            </section>

            <section>
              <p className="mb-4 font-medium text-xl">
                This is not just a platform. It is a call to redesign care, not around institutions, but around people.
              </p>
            </section>

            <section>
              <p className="mb-4">
                We are clinicians who have seen the gap up close. We are operators who know how to scale solutions. And we are partners looking to rebuild the broken bridge between medical possibility and public health reality.
              </p>
            </section>

            <section>
              <p className="mb-4">
                We believe AI, when grounded in compassion and community, can do more than automate tasks, it can extend reach. It can find those slipping through the cracks. It can transform detection into cure. But only if we build it with the right values, for the right reasons, and in the right places.
              </p>
            </section>

            <section>
              <p className="mb-4">
                This platform is just a start. A plan to turn clinical truth into public health transformation. A model not of what might be, but of what must be.
              </p>
            </section>

            <section className="border-l-4 border-indigo-600 pl-6 italic">
              <p className="mb-4">
                Because when the cure already exists, failure is no longer a mystery. It's a choice.
              </p>
              <p className="mb-4 font-semibold">
                And we choose to fix it.
              </p>
            </section>

            {/* Founders Section */}
            <section className="mt-16 pt-8 border-t border-gray-200 dark:border-gray-800">
              <div className="grid gap-8 md:grid-cols-2">
                <div className="text-center p-6 rounded-lg bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm">
                  <button
                    onClick={() => router.visit('/founders/saumil-patel')}
                    className="font-semibold text-lg text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Saumil J Patel, PharmD
                  </button>
                  <p className="text-sm text-muted-foreground mt-1">Co-Founder</p>
                </div>
                <div className="text-center p-6 rounded-lg bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm">
                  <button
                    onClick={() => router.visit('/founders/pooja-amin')}
                    className="font-semibold text-lg text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Pooja Amin, PharmD
                  </button>
                  <p className="text-sm text-muted-foreground mt-1">Co-Founder</p>
                </div>
              </div>
            </section>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <Button size="lg" onClick={() => router.visit('/')}>
            Back to Home
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-white/50 backdrop-blur-sm dark:bg-gray-950/50 dark:border-gray-800 mt-20">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-4 text-center text-sm text-muted-foreground md:flex-row md:justify-between">
            <p>&copy; 2026 Breakthrough Health. Empowering community health through technology.</p>
            <div className="flex gap-6">
              <button
                onClick={() => router.visit('/about')}
                className="hover:text-foreground transition-colors"
              >
                About Us
              </button>
              <button
                onClick={() => router.visit('/disclaimer')}
                className="hover:text-foreground transition-colors"
              >
                Disclaimer
              </button>
              <button
                onClick={() => router.visit('/privacy')}
                className="hover:text-foreground transition-colors"
              >
                Privacy Policy
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
